import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import { getImageMeta } from '../lib/mediaDescriptions';

export default function Referencer() {
  const [reviews, setReviews] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const carouselRef = useRef(null);

  const scroll = (direction) => {
    if (carouselRef.current) {
      const { scrollLeft, clientWidth } = carouselRef.current;
      // Calculate scroll step based on responsive visible cards
      const cardWidth = clientWidth / (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1);
      const scrollTo = direction === 'left' ? scrollLeft - cardWidth : scrollLeft + cardWidth;
      carouselRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Fetch reviews
    fetch('https://api.dev.auxo.dk/public/content/referencer')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setReviews(data);
        }
      })
      .catch((err) => console.error('Error fetching reviews:', err));

    // Fetch galleries
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data);
        }
      })
      .catch((err) => console.error('Error fetching galleries:', err));
  }, []);

  return (
    <div className="bg-brand-navy-50 min-h-screen">
      <SEO
        title="Referencer & Projekter | Entreprenør Jan Nørgaard"
        description="Se billeder og referencer fra vores arbejde med kloak, dræn, belægning og jordarbejde. Læs anmeldelser fra tilfredse kunder hos Jan Nørgaard ApS."
        image="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/belaegning-og-flisearbejde.jpg"
      />
      {/* Hero Section */}
      <section className="bg-brand-navy-900 text-white py-10 md:py-14 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/belaegning-og-flisearbejde.jpg"
            alt="Flisebelægning og anlægsarbejde udført af Entreprenør Jan Nørgaard ApS"
            title="Flisebelægning og anlæg"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
            Referencer & Kundeudtalelser
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-brand-navy-200">
            Hos{' '}
            <Link
              to="/"
              className="font-bold text-white underline decoration-brand-orange-400 decoration-2 underline-offset-4 hover:text-brand-orange-400 transition-colors"
              title="Entreprenør Jan Nørgaard - Til forsiden"
            >
              Entreprenør Jan Nørgaard ApS
            </Link>{' '}
            sætter vi en ære i veludført arbejde. Se billeder fra vores projekter og læs, hvad vores kunder siger om os.
          </p>
        </div>
      </section>

      {/* Galleries Section */}
      <section className="py-16 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="mt-3 font-display text-3xl font-bold uppercase text-brand-navy-900">
            Udførte projekter i billeder
          </h2>
          <p className="mt-4 text-brand-navy-600">
            Klik på et billede for at åbne det i stor visning. Vi opdaterer løbende vores gallerier med nye projekter.
          </p>
        </div>

        <div className="space-y-12">
          {galleries.map((gallery) => (
            <div
              key={gallery.id}
              className="py-6"
            >
              <h3 className="font-display text-xl font-bold uppercase text-brand-navy-900 mb-2 border-b border-brand-navy-100 pb-3 flex items-center gap-2">
                {gallery.title}
              </h3>
              {gallery.description?.da && (
                <p className="text-sm text-brand-navy-600 mb-6 leading-relaxed">
                  {gallery.description.da}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {gallery.images &&
                  gallery.images.map((imgUrl, idx) => {
                    const imgMeta = getImageMeta(imgUrl, gallery.title, idx + 1);
                    return (
                      <figure key={idx} className="group flex flex-col">
                        <button
                          type="button"
                          onClick={() => setLightboxImage({ url: imgUrl, ...imgMeta })}
                          className="relative aspect-video w-full overflow-hidden rounded-lg border border-brand-navy-100 bg-brand-navy-50 transition-transform hover:scale-[1.02] hover:shadow-md group cursor-pointer text-left"
                          title={imgMeta.title}
                          aria-label={`Vis forstørrelse af ${imgMeta.title}`}
                        >
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=250,fit=cover,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                            alt={imgMeta.alt}
                            title={imgMeta.title}
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 bg-brand-navy-900/60 opacity-0 transition-opacity group-hover:opacity-100 flex flex-col justify-between p-3 text-white">
                            <span className="text-xs font-semibold line-clamp-2">{imgMeta.title}</span>
                            <div className="flex items-center justify-end text-brand-orange-400">
                              <i className="fa-solid fa-magnifying-glass-plus text-base" />
                            </div>
                          </div>
                        </button>
                        <figcaption className="mt-1.5 px-0.5">
                          <span className="block text-xs font-semibold text-brand-navy-900 line-clamp-1">{imgMeta.title}</span>
                          {imgMeta.description && (
                            <span className="block text-[11px] text-brand-navy-600 line-clamp-1 mt-0.5" title={imgMeta.description}>
                              {imgMeta.description}
                            </span>
                          )}
                        </figcaption>
                      </figure>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews Grid Section */}
      <section className="py-20 bg-white border-t border-brand-navy-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up" className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-brand-navy-900 sm:text-4xl text-center md:text-left">
                Anbefalinger fra tilfredse kunder
              </h2>
            </div>
            <div className="flex justify-center gap-3 mt-6 md:mt-0">
              <button
                onClick={() => scroll('left')}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-navy-200 bg-white text-brand-navy-800 transition-colors hover:border-brand-orange-50 hover:bg-brand-orange-50 hover:text-brand-orange-600 cursor-pointer"
                aria-label="Forrige anmeldelse"
              >
                <i className="fa-regular fa-arrow-left text-lg" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-navy-200 bg-white text-brand-navy-800 transition-colors hover:border-brand-orange-50 hover:bg-brand-orange-50 hover:text-brand-orange-600 cursor-pointer"
                aria-label="Næste anmeldelse"
              >
                <i className="fa-regular fa-arrow-right text-lg" />
              </button>
            </div>
          </ScrollReveal>

          {/* Carousel container */}
          <ScrollReveal direction="up" delay={150}>
            <div
              ref={carouselRef}
              className="flex gap-6 overflow-x-auto scroll-smooth pb-4 scrollbar-none snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {reviews.map((review, idx) => (
                <div
                  key={idx}
                  className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] flex-shrink-0 snap-start rounded-lg bg-white p-6 shadow-md border border-brand-navy-100 flex flex-col justify-between transition-transform hover:-translate-y-1"
                >
                  <div>
                    <div className="flex gap-1 text-brand-orange-500 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="fa-solid fa-star text-sm" />
                      ))}
                    </div>
                    <p className="text-sm text-brand-navy-600 italic leading-relaxed">
                      "{review.text}"
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-brand-navy-100">
                    <span className="block text-sm font-bold text-brand-navy-900">
                      {review.name}
                    </span>
                    <span className="block text-xs text-brand-navy-500">
                      {review.company} {review.service && `· ${review.service}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Lightbox Overlay */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy-950/90 p-4 backdrop-blur-sm cursor-zoom-out"
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white text-3xl hover:text-brand-orange-400 p-2 cursor-pointer z-10"
            aria-label="Luk forstørret visning"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div
            className="relative max-h-[90vh] max-w-4xl w-full flex flex-col items-center cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.url || lightboxImage}
              alt={lightboxImage.alt || 'Forstørret visning af udført arbejde'}
              title={lightboxImage.title || 'Udført arbejde - Entreprenør Jan Nørgaard ApS'}
              className="max-h-[75vh] w-auto max-w-full object-contain rounded-t-lg shadow-2xl"
            />
            {lightboxImage.title && (
              <div className="w-full bg-brand-navy-900 text-white p-4 rounded-b-lg border-t border-brand-navy-700">
                <h4 className="font-display text-base font-bold text-white mb-1">
                  {lightboxImage.title}
                </h4>
                {lightboxImage.description && (
                  <p className="text-sm text-brand-navy-200">
                    {lightboxImage.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
